import {
  Box,
  Card,
  Center,
  Grid,
  Group,
  Overlay,
  Stack,
  Text,
} from "@mantine/core";
import {
  IconBarcode,
  IconClockHour3,
  IconMessageExclamation,
} from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { LBL_BACK_ORDER_ITEM } from "common/constants";
import { AddToCartControls } from "components/cart/AddToCart";
import { SafeImage } from "components/SafeImage ";
import { MainBodyWrapper } from "pages/Home.styles";
import { useSessionStore } from "stores/userSessionStore";
import { ProductSummaries } from "types/model.types";
import { getProductTotalPriceAsString } from "utils/helper";

type ProductCardProps = {
  title?: string;
  mainText?: string;
  products: ProductSummaries;
  span?: { base: number; sm?: number; md?: number };
};

const ProductCards = ({
  span,
  title,
  mainText,
  products,
}: ProductCardProps) => {
  const { isLoggedIn, priceLevel } = useSessionStore();
  const navigate = useNavigate();

  return (
    <MainBodyWrapper>
      {title && (
        <Text span size="xl" mb="md">
          <span style={{ fontWeight: 400 }}>
            {mainText ?? "Check out our "}
          </span>
          <span style={{ fontWeight: 700 }}>{` ${title}`}</span>
        </Text>
      )}

      <Grid gutter="md">
        {products.map((product) => (
          <Grid.Col
            key={product.id}
            span={span ?? { base: 12, sm: 6, md: 3, lg: 3 }}
          >
            <Card
              shadow="sm"
              padding="md"
              radius="md"
              withBorder
              style={{
                minHeight: 320,
                height: 340,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              {/* 🔴 Backorder badge */}
              {Boolean(product.backorder) && (
                <Box
                  style={{
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "150px",
                    transform: "translate(20%, 330%) rotate(45deg)",
                    transformOrigin: "top right",
                    backgroundColor: "#fa5252",
                    padding: "4px 34px",
                    color: "white",
                    fontWeight: 600,
                    fontSize: 12,
                    borderRadius: 4,
                    zIndex: 1,
                  }}
                >
                  {LBL_BACK_ORDER_ITEM}
                </Box>
              )}

              {/* 🖼️ Image section */}
              <Box pos="relative" h={140}>
                <Center>
                  <SafeImage
                    src={`/images/${product.featuredImageName}`}
                    onClick={(e) => {
                      navigate(`/products/details/${product.productCode}`);
                      e.preventDefault();
                    }}
                    style={{ cursor: "pointer" }}
                    alt={product.title}
                    height={120}
                    width={120}
                    fit="contain"
                  />
                </Center>

                {product.soh === 0 && (
                  <Overlay
                    color="rgba(0, 0, 0, 0.4)"
                    blur={0}
                    center
                    radius="md"
                    zIndex={0}
                    style={{ cursor: "pointer" }}
                    onClick={(e) => {
                      navigate(`/products/details/${product.productCode}`);
                      e.preventDefault();
                    }}
                  >
                    <Text
                      fw={900}
                      size="xl"
                      c="white"
                      style={{ textShadow: "0 0 8px rgba(0, 0, 0, 0.1)" }}
                    >
                      currently not available
                    </Text>
                  </Overlay>
                )}
              </Box>

              {/* 🏷️ Flags */}
              <Stack gap={2} align="center" mt="xs">
                {/* 🏷️ Product Flags */}
                <Group gap="xs">
                  <Text size="lg" c="red.7">
                    <IconBarcode size={14} /> {product.productCode}
                  </Text>
                  {product.shortDated && (
                    <Text size="xs" c="yellow.7">
                      <IconClockHour3 size={14} /> Short Dated
                    </Text>
                  )}
                  {product.onRequest && (
                    <Text size="xs" c="blue.6">
                      <IconMessageExclamation size={14} /> On Request
                    </Text>
                  )}
                </Group>
              </Stack>

              {/* 🧾 Title + Price */}
              <Stack align="center" gap={2} mt="xs">
                <Text fw={600} size="sm" ta="center">
                  {product.title}
                </Text>
                <Group gap="xs">
                  {isLoggedIn && (
                    <Text fw={900} size="md" ta="center">
                      {getProductTotalPriceAsString(product, priceLevel)}
                    </Text>
                  )}
                  <Text size="xs" c="dimmed">
                    {product.unitsCtn} / CTN
                  </Text>
                </Group>
              </Stack>

              {/* 📦 Footer controls */}
              <Group justify="space-between" mt="auto">
                {isLoggedIn && <AddToCartControls product={product} />}
              </Group>
            </Card>
          </Grid.Col>
        ))}
      </Grid>
    </MainBodyWrapper>
  );
};

export default ProductCards;
